import { Box, Card, Group, Progress, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconCheck, IconCircle, IconLock } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { ONBOARDING_SECTIONS } from './onboardingTypes';
import { useOnboardingPractitioner } from './useOnboardingPractitioner';
import { useOnboardingStatus } from './useOnboardingStatus';
import classes from './OnboardingHub.module.css';

const PRACTICE_NAME = 'Unify Mental Health';

export function OnboardingHub(): JSX.Element {
  const profile = useOnboardingPractitioner();
  const navigate = useNavigate();
  const onboardingStatus = useOnboardingStatus();

  const displayName = profile?.name?.[0]?.text ?? 'Provider';
  const percent = onboardingStatus.progressPercent;

  return (
    <Stack gap="xl">
      <Text size="xl" fw={600} c="gray.9">
        Hi, {displayName}.
      </Text>

      <Card
        shadow="sm"
        padding="lg"
        radius="md"
        withBorder
        style={{ backgroundColor: 'var(--unify-bg-paper)', borderColor: 'var(--mantine-color-gray-5)' }}
      >
        <Box mb="xs">
          <Text fw={700} size="lg" c="gray.9">
            Welcome to {PRACTICE_NAME}!
          </Text>
          <Text size="sm" c="dimmed">
            Start setting up your account now.
          </Text>
        </Box>

        <Group justify="space-between" mb="md">
          <Progress value={percent} size="lg" color="gold" style={{ flex: 1 }} aria-label={`Onboarding progress: ${percent}%`} />
          <Text size="sm" fw={500} c="gray.6" ml="sm">
            {percent}% Complete
          </Text>
        </Group>

        <Stack gap="sm">
          {ONBOARDING_SECTIONS.map((section) => {
            const complete = onboardingStatus.sections[section.id].complete;
            const unlocked = onboardingStatus.unlockedSections[section.id];
            const isCurrent = unlocked && !complete;

            const className = !unlocked
              ? classes.optionLocked
              : isCurrent
                ? classes.optionCurrent
                : classes.option;

            const icon = complete ? (
              <IconCheck size={20} style={{ color: 'var(--mantine-color-gold-6)', flexShrink: 0 }} />
            ) : !unlocked ? (
              <IconLock size={18} style={{ color: 'var(--mantine-color-gray-5)', flexShrink: 0 }} />
            ) : (
              <IconCircle size={20} style={{ color: 'var(--mantine-color-gray-5)', flexShrink: 0 }} />
            );

            const button = (
              <UnstyledButton
                key={section.id}
                className={className}
                onClick={unlocked ? () => navigate(section.path) : undefined}
                tabIndex={unlocked ? 0 : -1}
                aria-disabled={!unlocked}
              >
                <Group gap="sm" wrap="nowrap">
                  {icon}
                  <Box style={{ minWidth: 0 }}>
                    <Text fw={600} size="sm" c={!unlocked ? 'dimmed' : complete ? 'dimmed' : 'gray.8'}>
                      {section.title}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {section.description}
                    </Text>
                  </Box>
                  {!unlocked && (
                    <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                      Complete previous steps first
                    </Text>
                  )}
                </Group>
              </UnstyledButton>
            );

            return !unlocked ? (
              <Tooltip key={section.id} label="Complete the previous step to unlock" position="left" withArrow>
                {button}
              </Tooltip>
            ) : (
              button
            );
          })}
        </Stack>
      </Card>
    </Stack>
  );
}
